<template>
  <div class="review-panel">
    <h3 class="panel-title">{{ $t('review.reviewMode') }}</h3>

    <!-- Review Mode Selection -->
    <div class="mode-options">
      <label
        v-for="mode in modes"
        :key="mode.id"
        class="mode-option"
        :class="{ selected: selectedMode === mode.id }"
      >
        <input
          type="radio"
          :value="mode.id"
          v-model="selectedMode"
          class="mode-radio"
        />
        <div class="mode-info">
          <span class="mode-label">{{ mode.label }}</span>
          <span class="mode-desc">{{ mode.desc }}</span>
        </div>
      </label>
    </div>

    <!-- Mode-specific content -->
    <div class="mode-content" v-if="selectedMode">
      <!-- Approve Mode -->
      <template v-if="selectedMode === 'approve'">
        <div class="form-field">
          <label>{{ $t('review.reasoning') }}</label>
          <textarea
            v-model="reasoning"
            :placeholder="$t('review.reasoningPlaceholder')"
            class="textarea-input"
            rows="4"
          ></textarea>
        </div>
      </template>

      <!-- Adjust / Override Mode -->
      <template v-if="selectedMode === 'adjust' || selectedMode === 'override'">
        <div class="shares-editor">
          <div class="shares-editor-header">
            <span>{{ $t('result.heir') }}</span>
            <span>{{ $t('result.count') }}</span>
            <span>{{ $t('review.sharePercentage') }}</span>
            <span>Type</span>
          </div>
          <div
            v-for="(share, i) in editableShares"
            :key="i"
            class="shares-editor-row"
          >
            <span class="share-heir-name">{{ getHeirLabel(share.heir_category) }}</span>
            <input
              type="number"
              v-model.number="share.heir_count"
              min="0"
              class="share-number-input"
            />
            <input
              type="number"
              v-model.number="share.share_percentage"
              min="0"
              max="100"
              step="0.01"
              class="share-number-input"
            />
            <select v-model="share.heir_type" class="share-type-select">
              <option value="quota">{{ $t('result.quota') }}</option>
              <option value="residuary">{{ $t('result.residuary') }}</option>
              <option value="distant_kindred">{{ $t('result.distantKindred') }}</option>
            </select>
          </div>

          <!-- Total indicator -->
          <div class="shares-total" :class="{ valid: totalPct === 100, invalid: totalPct !== 100 }">
            {{ $t('review.currentTotal') }}: {{ totalPct.toFixed(2) }}%
            <span v-if="totalPct !== 100" class="total-warning">
              {{ $t('review.totalMustBe100') }}
            </span>
          </div>
        </div>

        <div class="form-field">
          <label>{{ $t('review.reasoning') }} *</label>
          <textarea
            v-model="reasoning"
            :placeholder="$t('review.reasoningPlaceholder')"
            class="textarea-input"
            rows="4"
            required
          ></textarea>
        </div>
      </template>

      <!-- Request Info Mode -->
      <template v-if="selectedMode === 'request_info'">
        <div class="form-field">
          <label>{{ $t('review.questions') }} *</label>
          <textarea
            v-model="questions"
            :placeholder="$t('review.questionsPlaceholder')"
            class="textarea-input"
            rows="4"
            required
          ></textarea>
        </div>
      </template>

      <!-- Document Upload (for decision modes, not request_info) -->
      <div v-if="selectedMode !== 'request_info'" class="form-field">
        <label>{{ $t('review.sealDocument') }}</label>
        <p class="field-hint">{{ $t('review.sealDocumentHint') }}</p>
        <div class="file-upload-area">
          <input
            type="file"
            accept="image/*"
            class="file-input"
            @change="handleFileSelect"
            ref="fileInputRef"
          />
          <div v-if="documentPreview" class="document-preview">
            <img :src="documentPreview" alt="Document preview" class="preview-image" />
            <button type="button" class="remove-file-btn" @click="removeFile">&times;</button>
          </div>
        </div>
      </div>

      <!-- Optional Notes (all modes) -->
      <div class="form-field">
        <label>{{ $t('review.notes') }}</label>
        <textarea
          v-model="notes"
          :placeholder="$t('review.notesPlaceholder')"
          class="textarea-input"
          rows="2"
        ></textarea>
      </div>

      <!-- Submit -->
      <IntercomButton
        variant="primary"
        :loading="isSubmitting"
        :disabled="!canSubmit"
        @click="handleSubmit"
      >
        {{ isSubmitting ? $t('review.submitting') : $t('review.submitReview') }}
      </IntercomButton>
    </div>
  </div>
</template>

<script setup>
import { ref, reactive, computed, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { IntercomButton } from '@shared/components/intercom'

const { t } = useI18n()

const props = defineProps({
  existingShares: { type: Array, default: () => [] },
  isSubmitting: { type: Boolean, default: false },
})

const emit = defineEmits(['submit'])

const selectedMode = ref(null)
const reasoning = ref('')
const questions = ref('')
const notes = ref('')
const documentFile = ref(null)
const documentPreview = ref(null)
const fileInputRef = ref(null)

const handleFileSelect = (event) => {
  const file = event.target.files?.[0]
  if (!file) return
  documentFile.value = file
  documentPreview.value = URL.createObjectURL(file)
}

const removeFile = () => {
  documentFile.value = null
  if (documentPreview.value) {
    URL.revokeObjectURL(documentPreview.value)
    documentPreview.value = null
  }
  if (fileInputRef.value) fileInputRef.value.value = ''
}

const modes = [
  { id: 'approve', label: t('review.approve'), desc: t('review.approveDesc') },
  { id: 'adjust', label: t('review.adjust'), desc: t('review.adjustDesc') },
  { id: 'override', label: t('review.override'), desc: t('review.overrideDesc') },
  { id: 'request_info', label: t('review.requestInfo'), desc: t('review.requestInfoDesc') },
]

// Editable shares for adjust/override
const editableShares = reactive([])

// Initialize editable shares when mode changes to adjust/override
watch(selectedMode, (mode) => {
  if (mode === 'adjust' || mode === 'override') {
    editableShares.length = 0
    const source = props.existingShares.filter((s) => !s.is_blocked)
    source.forEach((s) => {
      editableShares.push({
        heir_category: s.heir_category,
        heir_count: s.heir_count,
        share_percentage: s.share_percentage,
        heir_type: s.heir_type,
      })
    })
  }
})

const totalPct = computed(() => editableShares.reduce((sum, s) => sum + (s.share_percentage || 0), 0))

const heirLabelMap = {
  husband: 'heir.husband', wife: 'heir.wife', son: 'heir.son', daughter: 'heir.daughter',
  father: 'heir.father', mother: 'heir.mother', sons_son: 'heir.sonsSon',
  sons_daughter: 'heir.sonsDaughter', fathers_father: 'heir.fathersFather',
  paternal_grandmother: 'heir.paternalGrandmother', maternal_grandmother: 'heir.maternalGrandmother',
  full_brother: 'heir.fullBrother', full_sister: 'heir.fullSister',
  paternal_half_brother: 'heir.paternalHalfBrother', paternal_half_sister: 'heir.paternalHalfSister',
  maternal_half_brother: 'heir.maternalHalfBrother', maternal_half_sister: 'heir.maternalHalfSister',
  full_brothers_son: 'heir.fullBrothersSon', paternal_half_brothers_son: 'heir.paternalHalfBrothersSon',
  full_paternal_uncle: 'heir.fullPaternalUncle', paternal_half_uncle: 'heir.paternalHalfUncle',
  full_cousin: 'heir.fullCousin', paternal_half_cousin: 'heir.paternalHalfCousin',
}

const getHeirLabel = (category) => {
  const key = heirLabelMap[category]
  return key ? t(key) : category.replace(/_/g, ' ')
}

const canSubmit = computed(() => {
  if (!selectedMode.value) return false
  if (selectedMode.value === 'approve') return true
  if (selectedMode.value === 'adjust' || selectedMode.value === 'override') {
    return reasoning.value.trim() && Math.abs(totalPct.value - 100) < 0.01
  }
  if (selectedMode.value === 'request_info') return questions.value.trim()
  return false
})

const handleSubmit = () => {
  const payload = {
    mode: selectedMode.value,
    reasoning: reasoning.value.trim() || undefined,
    notes: notes.value.trim() || undefined,
    documentFile: documentFile.value || undefined,
  }

  if (selectedMode.value === 'adjust' || selectedMode.value === 'override') {
    payload.shares = editableShares.map((s) => ({
      heir_category: s.heir_category,
      heir_count: s.heir_count,
      share_percentage: s.share_percentage,
      heir_type: s.heir_type,
    }))
  }

  if (selectedMode.value === 'request_info') {
    payload.questions = questions.value.trim()
  }

  emit('submit', payload)
}
</script>

<style scoped>
.review-panel {
  display: flex;
  flex-direction: column;
  gap: var(--space-5);
}

.panel-title {
  font-size: var(--font-size-lg);
  font-weight: var(--font-weight-semibold);
  color: var(--color-charcoal);
  margin: 0;
}

/* Mode Options */
.mode-options {
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
}

.mode-option {
  display: flex;
  align-items: flex-start;
  gap: var(--space-3);
  padding: var(--space-3) var(--space-4);
  border: 1px solid var(--color-gray-200);
  border-radius: var(--radius-md);
  cursor: pointer;
  transition: all 0.15s ease;
}

.mode-option:hover {
  border-color: var(--color-blue);
}

.mode-option.selected {
  border-color: var(--color-blue);
  background: #eff6ff;
}

.mode-radio {
  margin-top: 3px;
}

.mode-info {
  display: flex;
  flex-direction: column;
}

.mode-label {
  font-size: var(--font-size-base);
  font-weight: var(--font-weight-semibold);
  color: var(--color-charcoal);
}

.mode-desc {
  font-size: var(--font-size-sm);
  color: var(--color-gray-500);
}

/* Mode Content */
.mode-content {
  display: flex;
  flex-direction: column;
  gap: var(--space-4);
  animation: slideDown 0.2s ease-out;
}

@keyframes slideDown {
  from { opacity: 0; transform: translateY(-8px); }
  to { opacity: 1; transform: translateY(0); }
}

.form-field {
  display: flex;
  flex-direction: column;
  gap: var(--space-1);
}

.form-field label {
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-medium);
  color: var(--color-gray-600);
}

.textarea-input {
  width: 100%;
  padding: var(--space-3);
  border: 1px solid var(--color-gray-300);
  border-radius: var(--radius-md);
  font-size: var(--font-size-base);
  color: var(--color-charcoal);
  font-family: inherit;
  resize: vertical;
  box-sizing: border-box;
}

.textarea-input:focus {
  outline: none;
  border-color: var(--color-blue);
  box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.15);
}

/* Shares Editor */
.shares-editor {
  border: 1px solid var(--color-gray-200);
  border-radius: var(--radius-md);
  overflow: hidden;
}

.shares-editor-header {
  display: grid;
  grid-template-columns: 2fr 60px 100px 100px;
  gap: var(--space-2);
  padding: var(--space-2) var(--space-3);
  background: var(--color-gray-50);
  font-size: var(--font-size-xs);
  font-weight: var(--font-weight-semibold);
  color: var(--color-gray-500);
  text-transform: uppercase;
}

.shares-editor-row {
  display: grid;
  grid-template-columns: 2fr 60px 100px 100px;
  gap: var(--space-2);
  padding: var(--space-2) var(--space-3);
  border-top: 1px solid var(--color-gray-100);
  align-items: center;
}

.share-heir-name {
  font-size: var(--font-size-sm);
  color: var(--color-charcoal);
}

.share-number-input {
  width: 100%;
  padding: var(--space-1) var(--space-2);
  border: 1px solid var(--color-gray-300);
  border-radius: var(--radius-sm);
  font-size: var(--font-size-sm);
  text-align: center;
}

.share-number-input:focus {
  outline: none;
  border-color: var(--color-blue);
}

.share-type-select {
  width: 100%;
  padding: var(--space-1) var(--space-2);
  border: 1px solid var(--color-gray-300);
  border-radius: var(--radius-sm);
  font-size: var(--font-size-xs);
  background: var(--color-white);
}

.shares-total {
  padding: var(--space-2) var(--space-3);
  border-top: 1px solid var(--color-gray-200);
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-semibold);
  text-align: right;
}

.shares-total.valid { color: #059669; }
.shares-total.invalid { color: var(--color-error); }

.total-warning {
  font-weight: normal;
  font-size: var(--font-size-xs);
  margin-left: var(--space-2);
}

/* File Upload */
.field-hint {
  font-size: var(--font-size-xs);
  color: var(--color-gray-500);
  margin: 0;
}

.file-upload-area {
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
}

.file-input {
  font-size: var(--font-size-sm);
  color: var(--color-gray-600);
}

.file-input::file-selector-button {
  padding: var(--space-1) var(--space-3);
  border: 1px solid var(--color-gray-300);
  border-radius: var(--radius-sm);
  background: var(--color-gray-50);
  color: var(--color-charcoal);
  font-size: var(--font-size-sm);
  cursor: pointer;
  margin-right: var(--space-2);
}

.file-input::file-selector-button:hover {
  background: var(--color-gray-100);
}

.document-preview {
  position: relative;
  display: inline-block;
  max-width: 200px;
}

.preview-image {
  width: 100%;
  border-radius: var(--radius-md);
  border: 1px solid var(--color-gray-200);
}

.remove-file-btn {
  position: absolute;
  top: -8px;
  right: -8px;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: var(--color-error);
  color: white;
  border: 2px solid white;
  font-size: 14px;
  line-height: 1;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
}
</style>
